import { createColumnHelper } from '@tanstack/react-table';
import { SettlementRuleResult } from '@mews-commander/data-access';
import { LocalizedEnum, LocalizedText, lt, showSuccessAlert } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Body, DropDownButton, DropdownItem, Icon, IconName, Link, Stack } from '@optimus-web/core';
import { UnicodeEntity } from '@mews/enums';
import { Dash } from '@mews-commander/ui';
import { copyToClipboard } from '@mews-commander/utils';
import { SettlementRuleTiming, SettlementRuleValue } from '../../../components';
import { useDeleteSettlementRuleDialog } from './useDeleteSettlementRuleDialog';
import { useUpdatePaymentPolicyDialogForm } from './useUpdatePaymentPolicyDialogForm';
import { PaymentPolicyStatus } from './PaymentPolicyStatus';

const columnHelper = createColumnHelper<SettlementRuleResult>();

interface WithSettlementRuleResult {
    settlementRuleResult: SettlementRuleResult;
}

const ActionCell = ({ settlementRuleResult }: WithSettlementRuleResult) => {
    const { confirmationDialog, openConfirmationDialog } = useDeleteSettlementRuleDialog({
        settlementRuleId: settlementRuleResult.id,
    });
    const [renderDialog, updateAction] = useUpdatePaymentPolicyDialogForm({ paymentPolicyId: settlementRuleResult.id });
    return (
        <>
            <DropDownButton icon={IconName.More} size="small" variant="ghost">
                <DropdownItem label={lt('Edit')} onClick={updateAction} />
                <DropdownItem label={lt('Delete')} onClick={openConfirmationDialog} variant="danger" />
                <DropdownItem
                    label={lt('CopyPaymentPolicyId')}
                    onClick={() => {
                        if (copyToClipboard(settlementRuleResult.id)) {
                            showSuccessAlert({ description: lt('CopiedSuccessMessage') });
                        }
                    }}
                />
            </DropDownButton>
            {confirmationDialog}
            {renderDialog()}
        </>
    );
};

export const getSettlementRulesColumns = ({ onNameClick }: { onNameClick: (id: string) => void }) => [
    columnHelper.accessor('name', {
        header: () => <LocalizedText textKey={TextKey.Name} />,
        cell: (props) => (
            <Link data-disable-click-away standalone variant="basic" onClick={() => onNameClick(props.row.id)}>
                <Stack spacing="100" itemAlignment="center">
                    {props.row.original.isPortfolioManaged ? <Icon value={IconName.CustomLink} size="small" /> : null}
                    <Body size="small" wordBreak="break-all" ellipsis maxLines={1} as="span">
                        {props.getValue() ?? UnicodeEntity.Dash}
                    </Body>
                </Stack>
            </Link>
        ),
    }),
    columnHelper.accessor('trigger', {
        header: () => <LocalizedText textKey={TextKey.SettlementRuleTiming} />,
        cell: (props) => <SettlementRuleTiming offset={props.row.original.offset} trigger={props.getValue()} />,
    }),
    columnHelper.accessor('value', {
        header: () => <LocalizedText textKey={TextKey.Amount} />,
        cell: (props) => <SettlementRuleValue {...props.getValue()} />,
    }),
    columnHelper.accessor('action', {
        header: () => <LocalizedText textKey={TextKey.Method} />,
        cell: (props) => <LocalizedEnum prefix="SettlementRuleAction" value={props.getValue()} />,
    }),
    columnHelper.accessor('type', {
        header: () => <LocalizedText textKey={TextKey.SettlementRuleMode} />,
        cell: (props) => <LocalizedEnum prefix="SettlementRuleType" value={props.getValue()} />,
    }),
    columnHelper.accessor('key', {
        header: () => <LocalizedText textKey={TextKey.Key} />,
        cell: (props) => props.getValue() || <Dash />,
    }),
    columnHelper.accessor('assignmentStatus', {
        header: () => <LocalizedText textKey={TextKey.Status} />,
        cell: (props) => <PaymentPolicyStatus status={props.getValue()} />,
    }),
    columnHelper.display({
        id: 'actions',
        meta: { align: 'end' },
        cell: (props) =>
            !props.row.original.isPortfolioManaged ? <ActionCell settlementRuleResult={props.row.original} /> : null,
    }),
];
